import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtTime, errMsg } from "@/lib/api";
import { AudioWave } from "@/components/AudioWave";
import { ConfBadge, Empty, StatusPill } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMicTranscription } from "@/hooks/useMicTranscription";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Play, Pause, Square, Radio, Mic2, Loader2, Sparkles, Languages, Users, Gauge, RotateCcw, MicOff, Cpu, AlertTriangle,
} from "lucide-react";

const MODES = [
  { value: "mic", label: "Mikrofon (Realtime)" },
  { value: "sim", label: "Simulasi Demo" },
];

export default function LiveMeeting() {
  const { id } = useParams();
  const nav = useNavigate();
  const mic = useMicTranscription();
  const { user } = useAuth();

  const [mode, setMode] = useState("mic");
  const [meetings, setMeetings] = useState([]);
  const [meetingId, setMeetingId] = useState(id || "");
  const [starting, setStarting] = useState(false);

  // simulation state
  const [simState, setSimState] = useState("idle");
  const [simSegs, setSimSegs] = useState([]);
  const [simElapsed, setSimElapsed] = useState(0);
  const [simSpeakers, setSimSpeakers] = useState([]);
  const idxRef = useRef(0);
  const elapsedRef = useRef(0);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const feedRef = useRef(null);

  useEffect(() => {
    api.get("/meetings").then(({ data }) => {
      setMeetings(data);
      if (!id && data.length && !meetingId) setMeetingId(data[0].id);
    }).catch((e) => toast.error(errMsg(e)));
    return () => { clearInterval(timerRef.current); clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (!mic.supported && mode === "mic") setMode("sim"); /* eslint-disable-next-line */ }, [mic.supported]);
  useEffect(() => { if (mic.error) toast.error(mic.error); }, [mic.error]);
  useEffect(() => { if (mic.result) toast.success(`Rapat selesai · ${mic.result.segments} segmen tersimpan`); }, [mic.result]);

  const isMic = mode === "mic";
  const segs = isMic ? mic.segments : simSegs;
  const elapsed = isMic ? mic.elapsed : simElapsed;
  const speakers = isMic ? mic.speakers : simSpeakers;
  const state = isMic
    ? (mic.status === "recording" ? "live" : mic.status === "paused" ? "paused" : mic.status === "stopped" ? "stopped" : mic.status === "connecting" ? "connecting" : "idle")
    : simState;
  const active = state === "live";

  useEffect(() => { if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight; }, [segs]);

  // ---------------------------------------------------------------- simulation
  const simTick = () => {
    timerRef.current = setInterval(() => { elapsedRef.current += 1; setSimElapsed(elapsedRef.current); }, 1000);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.post(`/meetings/${meetingId}/live/segment`, { index: idxRef.current, elapsed: elapsedRef.current });
        idxRef.current += 1;
        setSimSegs((p) => [...p, data]);
      } catch (e) { void e; }
    }, 3200);
  };

  const start = async () => {
    if (!meetingId) { toast.error("Pilih rapat terlebih dahulu"); return; }
    setStarting(true);
    try {
      if (isMic) {
        const ok = await mic.start(meetingId, user?.id || "").catch(() => false);
        if (ok) toast.success("Mikrofon aktif — transkripsi realtime dimulai");
      } else {
        const { data } = await api.post(`/meetings/${meetingId}/live/start`);
        setSimSpeakers(data.speakers || []);
        setSimSegs([]); idxRef.current = 0; elapsedRef.current = 0; setSimElapsed(0);
        setSimState("live"); simTick();
        toast.success("Sesi simulasi dimulai");
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setStarting(false); }
  };

  const pause = () => {
    if (isMic) { mic.pause(); toast.info("Perekaman dijeda"); return; }
    clearInterval(timerRef.current); clearInterval(pollRef.current); setSimState("paused"); toast.info("Sesi dijeda");
  };

  const resume = () => {
    if (isMic) { mic.resume(); toast.success("Perekaman dilanjutkan"); return; }
    setSimState("live"); simTick(); toast.success("Sesi dilanjutkan");
  };

  const stop = async () => {
    if (isMic) { mic.stop(); return; }
    clearInterval(timerRef.current); clearInterval(pollRef.current);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/live/stop`);
      setSimState("stopped");
      toast.success(`Rapat selesai · ${data.segments} segmen tersimpan`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reset = () => {
    if (isMic) { mic.reset(); return; }
    setSimState("idle"); setSimSegs([]); setSimElapsed(0);
  };

  const avgConf = segs.length ? segs.reduce((a, s) => a + s.confidence, 0) / segs.length : 0;
  const current = meetings.find((m) => m.id === meetingId);

  return (
    <div className="space-y-7" data-testid="live-meeting-page">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="label-xs">Realtime Engine</p>
          <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Realtime Transcript</h1>
          <p className="mt-2 text-sm text-slate-500">
            {isMic
              ? "Mikrofon → WebSocket → Whisper (whisper-1). Transkrip muncul setiap ±7 detik, audio direkam tanpa jeda."
              : "Mode simulasi memutar skenario rapat tanpa mikrofon (untuk demo)."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={mode} onValueChange={setMode} disabled={active || state === "connecting"}>
            <SelectTrigger className="h-11 w-[210px] rounded-xl" data-testid="live-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODES.map((m) => (
                <SelectItem key={m.value} value={m.value} disabled={m.value === "mic" && !mic.supported}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={meetingId} onValueChange={(v) => { setMeetingId(v); nav(`/live/${v}`); }} disabled={active || state === "connecting"}>
            <SelectTrigger className="h-11 w-[270px] rounded-xl" data-testid="live-meeting-select"><SelectValue placeholder="Pilih rapat" /></SelectTrigger>
            <SelectContent>
              {meetings.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
            </SelectContent>
          </Select>
          {current && <Button variant="outline" className="h-11 rounded-xl border-slate-200" onClick={() => nav(`/meetings/${meetingId}`)} data-testid="open-meeting-detail-btn">Detail Rapat</Button>}
        </div>
      </div>

      {!mic.supported && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="mic-unsupported-warning">
          <MicOff size={17} className="mt-0.5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">Browser ini tidak mendukung perekaman mikrofon. Gunakan mode Simulasi Demo, atau buka VECTRA di Chrome/Edge terbaru.</p>
        </div>
      )}

      {/* Console */}
      <div className="clay overflow-hidden" data-testid="live-console">
        <div className="relative p-8 bg-brand-900">
          <div className="absolute inset-0 grad-brand opacity-40" />
          <div className="relative z-10">
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div>
                <div className="flex items-center gap-2.5">
                  <span className={`h-2 w-2 rounded-full ${active ? "bg-red-500 animate-pulse" : state === "connecting" ? "bg-amber-400 animate-pulse" : "bg-slate-400"}`} />
                  <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-white/70">
                    {active ? (isMic ? "Recording · Mic Live" : "Recording · Simulasi")
                      : state === "connecting" ? "Menyambungkan…"
                      : state === "paused" ? "Paused"
                      : state === "stopped" ? "Session Ended" : "Standby"}
                  </p>
                </div>
                <p className="mt-3 font-mono text-4xl font-semibold text-white tabular-nums" data-testid="live-timer">{fmtTime(elapsed)}</p>
                <p className="mt-2 text-sm text-white/70 max-w-md truncate">{current?.title || "Belum ada rapat dipilih"}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                {state === "idle" || state === "stopped" || state === "error" ? (
                  <Button onClick={start} disabled={starting || !meetingId || state === "connecting"} className="h-11 rounded-xl bg-white text-brand-700 hover:bg-slate-100 px-6 font-semibold" data-testid="live-start-btn">
                    {starting ? <Loader2 size={16} className="mr-2 animate-spin" /> : isMic ? <Mic2 size={16} className="mr-2" /> : <Radio size={16} className="mr-2" />}
                    {isMic ? "Start Recording" : "Start Meeting"}
                  </Button>
                ) : state === "connecting" ? (
                  <Button disabled className="h-11 rounded-xl bg-white/20 text-white px-6"><Loader2 size={16} className="mr-2 animate-spin" /> Menyambungkan</Button>
                ) : (
                  <>
                    {active ? (
                      <Button onClick={pause} className="h-11 rounded-xl bg-white/15 text-white hover:bg-white/25 px-5" data-testid="live-pause-btn">
                        <Pause size={16} className="mr-2" /> Pause
                      </Button>
                    ) : (
                      <Button onClick={resume} className="h-11 rounded-xl bg-white text-brand-700 hover:bg-slate-100 px-5" data-testid="live-resume-btn">
                        <Play size={16} className="mr-2" /> Resume
                      </Button>
                    )}
                    <Button onClick={stop} className="h-11 rounded-xl bg-red-500 text-white hover:bg-red-600 px-5" data-testid="live-stop-btn">
                      <Square size={15} className="mr-2" /> Stop
                    </Button>
                  </>
                )}
                {(state === "stopped" || state === "error") && (
                  <Button variant="ghost" onClick={reset} className="h-11 rounded-xl text-white hover:bg-white/15" data-testid="live-reset-btn">
                    <RotateCcw size={15} className="mr-2" /> Reset
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-8">
              <AudioWave active={active} bars={72} height={84} levels={isMic && (active || state === "connecting") ? mic.levels : null} />
            </div>
            {isMic && mic.pending > 0 && (
              <p className="mt-4 flex items-center justify-center gap-2 text-xs text-white/70" data-testid="live-pending">
                <Cpu size={13} className="animate-pulse" /> {mic.pending} potongan audio sedang ditranskripsi…
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-slate-100 border-t border-slate-100">
          {[
            ["Segmen", segs.length, Mic2],
            ["Confidence Rata-rata", segs.length ? `${(avgConf * 100).toFixed(1)}%` : "—", Gauge],
            ["Bahasa Terdeteksi", segs.length ? (segs[segs.length - 1].language || "id").toUpperCase() : "—", Languages],
            ["Speaker Terdaftar", speakers.length, Users],
          ].map(([label, value, Icon], i) => (
            <div key={label} className="p-5" data-testid={`live-stat-${i}`}>
              <div className="flex items-center gap-2 text-slate-400"><Icon size={14} /><span className="label-xs">{label}</span></div>
              <p className="mt-2 font-display text-xl font-semibold text-slate-900">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Live feed */}
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="clay p-6" data-testid="live-transcript-feed">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="label-xs">Live Transcript</p>
              <p className="mt-1 text-sm text-slate-500">Auto save aktif · setiap segmen langsung tersimpan ke database</p>
            </div>
            {active && <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-600"><span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />Streaming</span>}
          </div>
          <div ref={feedRef} className="max-h-[480px] overflow-y-auto space-y-1 pr-1">
            {segs.length === 0 ? (
              <Empty
                icon={isMic ? Mic2 : Radio}
                title={isMic ? "Belum ada suara terdeteksi" : "Belum ada transkrip realtime"}
                sub={isMic
                  ? "Tekan Start Recording dan izinkan akses mikrofon. Transkrip pertama muncul setelah ±7 detik bicara."
                  : "Tekan Start Meeting untuk memulai skenario simulasi dengan deteksi speaker otomatis."}
              />
            ) : segs.map((s) => (
              <div key={s.id} className="flex gap-4 rounded-xl border-l-2 border-brand-100 px-4 py-3.5 animate-fade-up" data-testid={`live-segment-${s.seq}`}>
                <span className="shrink-0 font-mono text-xs text-slate-400 pt-0.5 w-[62px]">{fmtTime(s.start)}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm font-semibold ${s.is_unknown ? "text-amber-600" : "text-slate-800"}`}>{s.speaker_name}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">{s.speaker_label}</span>
                    <ConfBadge value={s.confidence} label="ASR " />
                    <ConfBadge value={s.voice_confidence} label="Voice " />
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{s.text}</p>
                </div>
              </div>
            ))}
            {active && (
              <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400">
                <span className="flex gap-1">
                  {[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 rounded-full bg-brand-400 animate-bounce" style={{ animationDelay: `${i * 120}ms` }} />)}
                </span>
                Mendengarkan…
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="clay p-6" data-testid="live-speakers-card">
            <p className="label-xs">Speaker Detection</p>
            <div className="mt-4 space-y-3">
              {speakers.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada peserta terdaftar pada rapat ini. Tambahkan peserta agar biometrik suara aktif.</p>
              ) : speakers.map((sp, i) => {
                const count = segs.filter((s) => s.speaker_name === sp.name).length;
                const isActive = segs.length && segs[segs.length - 1].speaker_name === sp.name;
                return (
                  <div key={sp.user_id || i} className={`flex items-center gap-3 rounded-xl border p-3.5 transition-colors ${isActive ? "border-brand-600 bg-brand-50/60" : "border-slate-200/70"}`} data-testid={`live-speaker-${i}`}>
                    <span className="relative grid place-items-center h-9 w-9 rounded-full grad-brand text-white text-[11px] font-semibold">
                      {sp.name.slice(0, 2).toUpperCase()}
                      {isActive && <span className="absolute inset-0 rounded-full border-2 border-brand-500 animate-pulse-ring" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">{sp.name}</p>
                      <p className="text-xs text-slate-500">{count} segmen · {sp.enrolled ? "voiceprint aktif" : "belum enroll"}</p>
                    </div>
                    {sp.enrolled && <Mic2 size={14} className="text-emerald-500" />}
                  </div>
                );
              })}
            </div>
          </div>

          {state === "stopped" && (
            <div className="clay p-6" data-testid="post-session-card">
              <p className="label-xs">Langkah Selanjutnya</p>
              <p className="mt-3 text-sm text-slate-600">Transkrip tersimpan. Hasilkan ringkasan AI dan action items untuk rapat ini.</p>
              <Button className="mt-5 w-full rounded-xl grad-brand text-white" onClick={() => nav(`/meetings/${meetingId}`)} data-testid="goto-summary-btn">
                <Sparkles size={15} className="mr-2" /> Buka & Generate Summary
              </Button>
            </div>
          )}

          {mic.error && (
            <div className="clay p-6 border-red-200" data-testid="mic-error-card">
              <div className="flex items-start gap-3">
                <AlertTriangle size={17} className="mt-0.5 shrink-0 text-red-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Perekaman gagal</p>
                  <p className="mt-1 text-sm text-slate-600">{mic.error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="clay p-6">
            <p className="label-xs">Pipeline AI</p>
            <ol className="mt-4 space-y-2.5">
              {["Noise Reduction", "Voice Activity Detection", "Speech Recognition", "Speaker Diarization", "Voice Biometrics", "Punctuation Restoration", "Realtime Transcript"].map((s, i) => (
                <li key={s} className="flex items-center gap-3 text-sm text-slate-600">
                  <span className={`grid place-items-center h-6 w-6 shrink-0 rounded-lg text-[10px] font-bold ${active ? "grad-brand text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {current && <div className="flex items-center gap-3 text-sm text-slate-500"><StatusPill status={current.status} /> Status rapat saat ini</div>}
    </div>
  );
}
